import React from 'react';
import { EsteiraParalelaDoCarro } from '../../interfaces/Car';
import '../../shared/ui/badge-status.css';
import './selo-esteira-paralela.css';

interface SeloEsteiraParalelaProps {
  /** As paralelas VIGENTES do veículo. Lista vazia/ausente ⇒ nada é renderizado. */
  esteirasParalelas?: EsteiraParalelaDoCarro[] | null;
  /**
   * Quando informado, o selo vira **botão** e abre o controle das paralelas.
   *
   * Opcional de propósito: onde não há a quem abrir o controle (uma listagem, ou um papel que
   * não alcança a rota), o selo continua sendo um rótulo. Selo clicável que não faz nada é pior
   * que selo inerte.
   */
  onClick?: () => void;
}

/**
 * Selo "+ Mecânica" (at-99, ADR 0050), ao lado da placa nos modais e nas listagens.
 *
 * Diz o que a coluna "Esteira" não consegue dizer: o veículo está em Desmontagem **e** tem
 * serviço acontecendo em Mecânica ao mesmo tempo. Sem o selo, quem olha a lista vê o veículo
 * parado numa etapa e não sabe que há outro setor trabalhando nele — que é exatamente a
 * invisibilidade que a oficina resolvia movendo o carro de um lado para o outro.
 *
 * Molde do selo de pós-evento (at-96) e do selo de motivo da pendência (at-89): CSS
 * compartilhado (`shared/ui/badge-status.css`), nunca classe de status própria por tela.
 *
 * ⚠️ **Célula de tabela é UMA linha** (regra do Victor, 12/08/2026): o selo vai AO LADO da
 * placa, nunca empilhado, e com `nowrap`.
 *
 * ⚠️ Com mais de uma paralela, mostra UM selo com a contagem em vez de N selos: N selos
 * empurrariam a placa para a linha de baixo, e o detalhe completo está no `title` e no modal.
 */
const SeloEsteiraParalela: React.FC<SeloEsteiraParalelaProps> = ({
  esteirasParalelas,
  onClick,
}) => {
  if (!esteirasParalelas || esteirasParalelas.length === 0) return null;

  const nomes = esteirasParalelas.map((p) => p.nome).filter(Boolean);
  if (nomes.length === 0) return null;

  const rotulo = nomes.length === 1 ? `+ ${nomes[0]}` : `+ ${nomes.length} esteiras`;
  const detalhe = detalheDoSelo(esteirasParalelas);

  if (onClick) {
    return (
      <button
        type="button"
        className="cilia-badge badge-azul selo-esteira-paralela selo-esteira-paralela--botao"
        title={`${detalhe}. Clique para ver e controlar.`}
        onClick={(evento) => {
          // O selo vive no cabeçalho de modais que fecham por clique fora: sem isto, o clique
          // sobe e o hospedeiro pode tratá-lo como clique de fundo.
          evento.stopPropagation();
          onClick();
        }}
      >
        {rotulo}
      </button>
    );
  }

  return (
    <span className="cilia-badge badge-azul selo-esteira-paralela" title={detalhe}>
      {rotulo}
    </span>
  );
};

/** O texto do hover: o que está em curso em paralelo, e desde quando. */
export function detalheDoSelo(paralelas: EsteiraParalelaDoCarro[]): string {
  const partes = paralelas.map((p) => {
    const desde = p.abertaEm ? new Date(p.abertaEm).toLocaleDateString('pt-BR') : null;
    return desde ? `${p.nome} (desde ${desde})` : p.nome;
  });

  return `Serviço em paralelo, além da etapa atual: ${partes.join('; ')}`;
}

export default SeloEsteiraParalela;
